import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import PodcastEpisodeMetadata from '../components/PodcastEpisodeMetadata/PodcastEpisodeMetadata';
import * as Constants from '../constants';
import { getInDomain } from '../domains/externalUtils';

import { getArt } from './ArtUtils';
import { getFromMap } from './CoreUtils';
import { getAllAnnotations, getAnnotationsById, getDetails } from './EntityUtils';
import { getGenre, getGenreByPandoraId, getPlaylistFromState } from './MusicUtils';
import { decodeHtml } from './StringUtils';
import { formatTime } from './TimeUtils';

export function openTwitterComposer(message) {
  const encodedMessage = encodeURIComponent(message);
  window.open(`https://twitter.com/intent/tweet?text=${encodedMessage}`);
}

function getMetaTagDescription(sourceType, sourceName, authorName, sourceDescription) {
  const authorString = authorName ? ` by ${authorName}` : '';
  const sourceTypePartial = sourceType ? `the ${sourceType} ` : '';
  return sourceDescription
    ? sourceDescription
    : `Listen to ${sourceTypePartial}${sourceName}${authorString} on Pandora`;
}

function getStationMetaTagDescription(sourceName, sourceDescription) {
  if (sourceName === '') {
    return '';
  }
  // Stations don't include sourceType in the description because it's assumed they have 'Radio' in the name
  const sourceNameString =
    sourceName && sourceName.toLowerCase().includes('radio') ? sourceName : `${sourceName} Radio`;
  return sourceDescription ? sourceDescription : `Listen to ${sourceNameString} on Pandora`;
}

function getMetaTagDetails(state, paths, pathSegmentOne, pathSegmentTwo) {
  let metaTagTitle;
  let metaTagDescription = Constants.ShareModal.DefaultMetaTagDescription;
  let sourceType;
  let authorName;

  const pathName = Constants.MetaTags.pathName;
  const pandoraId = paths.length > 0 ? paths[paths.length - 1] : '';
  switch (pathSegmentOne) {
    case pathName.ALBUM:
    case pathName.ARTIST:
    case pathName.SONG:
    case pathName.PLAYLIST: {
      const getAnnotations = getInDomain('catalog.utils.getAnnotations');
      const getAnnotationByEncryptedPandoraId = getInDomain(
        'catalog.utils.getAnnotationByEncryptedPandoraId',
      );
      let activeAnnotations = pandoraId.includes(':')
        ? getAnnotations(state, pandoraId)
        : getAnnotationByEncryptedPandoraId(state, pandoraId);
      if (activeAnnotations.size === 0) {
        // For the case when playlist can only be found in `entities.playlists`
        activeAnnotations = getPlaylistFromState(state, pandoraId);
      }
      if (activeAnnotations) {
        sourceType = Constants.SourceTypes.DeepLinkContentTypes[activeAnnotations.get('type')];
        sourceType = sourceType ? sourceType.toLowerCase() : sourceType;
        sourceType = sourceType === 'track' ? 'song' : sourceType;
        const owner = getAnnotations(state, activeAnnotations.get('listenerPandoraId'));
        authorName = owner.isEmpty()
          ? activeAnnotations.get('artistName')
          : owner.get('displayname') || owner.get('webname');
        metaTagTitle = activeAnnotations.get('name');
        metaTagDescription = getMetaTagDescription(sourceType, metaTagTitle, authorName);
      }
      break;
    }
    case pathName.PODCAST: {
      const annotations = getAnnotationsById(state, pandoraId);
      if (annotations) {
        sourceType = Constants.SourceTypes.DeepLinkContentTypes[annotations.get('type')];
        sourceType = sourceType ? sourceType.toLowerCase() : sourceType;
        const catalogDetails = getDetails(state, pandoraId);
        const sourceDescription =
          getFromMap(catalogDetails, 'summary') || annotations.get('summary');
        authorName = annotations.get('publisherName');
        metaTagTitle = annotations.get('name');
        metaTagDescription = getMetaTagDescription(
          sourceType,
          metaTagTitle,
          authorName,
          sourceDescription,
        );
      }
      break;
    }
    default:
      break;
  }

  let alternateText = 'Pandora';
  if (pathSegmentTwo === Constants.App.UrlSegments.PLAY) {
    alternateText = 'Now Playing on Pandora';
  }
  const titleText = metaTagTitle ? metaTagTitle : alternateText;
  const title = decodeHtml(titleText);
  const description = metaTagDescription ? decodeHtml(metaTagDescription) : '';
  return {
    title,
    description,
  };
}

function getStationMetaTagDetails(state, paths, pathSegmentOne, pathSegmentTwo, genreStationName) {
  let metaTagTitle;
  let metaTagDescription;

  const pathName = Constants.MetaTags.pathName;
  switch (pathSegmentOne) {
    case pathName.GENRE: {
      let sourceDescription;
      const genreSeoToken = pathSegmentTwo;
      const genre = getGenre(state, genreSeoToken);
      if (genre) {
        metaTagTitle = genre.get('name');
        sourceDescription = genre.get('description');
      } else {
        metaTagTitle = genreStationName;
      }
      metaTagDescription = getStationMetaTagDescription(metaTagTitle, sourceDescription);
      break;
    }
    case pathName.STATION: {
      let stationId = paths.length > 0 ? paths[1] : '';
      if (paths.includes(Constants.App.UrlSegments.PLAY)) {
        stationId = !paths.includes(Constants.Catalog.SourceTypeUrlParams.THUMBED_UP)
          ? paths[2]
          : paths[3] && paths[3].split && paths[3].split(':').pop();
      }
      metaTagTitle = getInDomain('playback.utils.getStationName', '')(state, stationId);
      metaTagDescription = getStationMetaTagDescription(metaTagTitle);
      break;
    }
    default:
      break;
  }

  let alternateText = 'Pandora';
  if (pathSegmentTwo === Constants.App.UrlSegments.PLAY) {
    alternateText = 'Now Playing on Pandora';
  }

  const titleText =
    metaTagTitle === 'undefined' || isUndefined(metaTagTitle) || metaTagTitle === ''
      ? alternateText
      : metaTagTitle;
  const title = decodeHtml(titleText);
  const description = decodeHtml(metaTagDescription);
  return {
    title,
    description,
  };
}

function getMetaDetails(state, paths, pathSegmentOne, pathSegmentTwo, genreStationName) {
  let metaDetails;
  switch (pathSegmentOne) {
    case Constants.MetaTags.pathName.GENRE:
    case Constants.MetaTags.pathName.STATION:
      metaDetails = getStationMetaTagDetails(
        state,
        paths,
        pathSegmentOne,
        pathSegmentTwo,
        genreStationName,
      );
      break;
    case Constants.MetaTags.pathName.ALBUM:
    case Constants.MetaTags.pathName.ARTIST:
    case Constants.MetaTags.pathName.SONG:
    case Constants.MetaTags.pathName.PLAYLIST:
    case Constants.MetaTags.pathName.PODCAST:
      metaDetails = getMetaTagDetails(state, paths, pathSegmentOne, pathSegmentTwo);
      break;
    default:
      metaDetails = {
        title: Constants.MetaTags.metaTagsDefaultTitle,
        description: Constants.MetaTags.metaTagsDefaultDescription,
      };
      break;
  }
  return metaDetails;
}

export function getMetaTagDetailsForURL(state, pathname, genreStationName) {
  const paths = pathname.split('?')[0].replace(/^\//, '').split('/');
  const [pathSegmentOne, pathSegmentTwo] = paths;

  return getMetaDetails(state, paths, pathSegmentOne, pathSegmentTwo, genreStationName);
}

export function getSharePlaylistData(state, parentSourceId) {
  const data = {};
  const allAnnotations = getAllAnnotations(state);
  const playlist = getPlaylistFromState(state, parentSourceId);
  if (playlist) {
    // basic playlist details
    data.parentSourcePath = playlist.get('shareableUrlPath');
    data.parentSourceName = playlist.get('name');
    data.parentImgUrl = getArt(playlist, Constants.Grid.GridConstants.GRID_ITEM_IMAGE_SIZE, state);
    data.parentTrackCount = playlist.get('totalTracks');
    // Get playlist owner webname
    const playlistOwnerPandoraID = playlist.get('listenerPandoraId');
    const playlistOwnerAnnotations = allAnnotations.get(playlistOwnerPandoraID);
    if (playlistOwnerAnnotations) {
      data.parentCreatorName =
        playlistOwnerAnnotations.get('displayname') || playlistOwnerAnnotations.get('webname');
    }
  }
  return data;
}

export function getSharePodcastData(state, parentSourceId) {
  const data = {};
  const allAnnotations = getAllAnnotations(state);
  const podcastAnnotations = parentSourceId && allAnnotations.get(parentSourceId);
  if (podcastAnnotations) {
    data.parentSourcePath = podcastAnnotations.get('shareableUrlPath');
    data.parentSourceName = podcastAnnotations.get('name');
    data.parentImgUrl = getArt(
      podcastAnnotations,
      Constants.Grid.GridConstants.GRID_ITEM_IMAGE_SIZE,
      state,
    );
    data.parentEpisodeCount = podcastAnnotations.get('episodeCount');
    data.parentCreatorName = podcastAnnotations.get('publisherName');
  }
  return data;
}

export function getShareEpisodeData(state, childId) {
  const data = {};
  const allAnnotations = getAllAnnotations(state);
  const episodeAnnotations = childId && allAnnotations.get(childId);
  if (episodeAnnotations) {
    data.childSourceType = Constants.SourceTypes.PandoraTypes.EPISODE;
    data.childSourcePath = episodeAnnotations.get('shareableUrlPath');
    data.childName = episodeAnnotations.get('name');
    data.childImgUrl = getArt(
      episodeAnnotations,
      Constants.Grid.GridConstants.GRID_ITEM_IMAGE_SIZE,
      state,
    );
    data.childCreatorName = episodeAnnotations.get('programName');
    data.childReleaseDate = episodeAnnotations.get('releaseDate');
    data.childDuration = episodeAnnotations.get('duration');
  }
  return data;
}

export function getShareAlbumData(state, parentSourceId) {
  const data = {};
  const allAnnotations = getAllAnnotations(state);
  const albumAnnotations = allAnnotations.get(parentSourceId);
  if (albumAnnotations) {
    data.parentSourcePath = albumAnnotations.get('shareableUrlPath');
    data.parentSourceName = albumAnnotations.get('name');
    data.parentImgUrl = getArt(
      albumAnnotations,
      Constants.Grid.GridConstants.GRID_ITEM_IMAGE_SIZE,
      state,
    );
    data.parentTrackCount = albumAnnotations.get('trackCount');
    data.parentCreatorName = albumAnnotations.get('artistName');
    data.albumArtistId = albumAnnotations.get('artistId');
    const albumArtistAnnotations = allAnnotations.get(data.albumArtistId);
    if (albumArtistAnnotations) {
      data.albumArtistTwitterHandle = albumArtistAnnotations.get('twitterHandle');
    } else {
      data.needAlbumArtistAnnotations = true;
    }
  }
  return data;
}

export function getShareStationData(state, parentSourceId) {
  const data = {};
  let station;
  const allAnnotations = getAllAnnotations(state);
  data.isGenreStation = getInDomain('playback.utils.isGenrePandoraId')(parentSourceId);
  if (data.isGenreStation) {
    station = getGenreByPandoraId(state, parentSourceId) || allAnnotations.get(parentSourceId);
    const seed = getInDomain('playback.utils.getSeedByPandoraId')(state, parentSourceId);
    data.genreMusicId = seed ? seed.get('musicId') : null;
  } else {
    station =
      getInDomain('playback.utils.getStationByStationId')(state, parentSourceId) ||
      allAnnotations.get(parentSourceId);
  }
  if (station) {
    // If stationId is a pandoraId (SF), share seedId backstage page
    if (getInDomain('playback.utils.isStationPandoraId')(parentSourceId)) {
      data.parentSourcePath = station.get('shareableUrlPath');
    } else if (data.isGenreStation) {
      data.parentSourcePath = station.get('shareableUrlPath') || `/genre/${station.get('token')}`;
    } else {
      data.parentSourcePath = `/station/play/${parentSourceId}`;
    }

    if (getInDomain('playback.utils.isThumbprintStationId')(state, parentSourceId)) {
      data.parentSourceName = station.get('shareName');
    } else {
      data.parentSourceName = getInDomain('playback.utils.getName')(station);
    }
    data.parentImgUrl = getArt(station, Constants.Grid.GridConstants.GRID_ITEM_IMAGE_SIZE, state);

    // Fetch artistId if the seed is based on ARTIST sourceType
    const seedId = station.get('initialSeed');
    const seed = getInDomain('playback.utils.getSeedById')(state, seedId);
    const pandoraId = (seed && seed.get('pandoraId')) || station.get('seedId');
    const { ARTIST, COMPOSER } = Constants.SourceTypes.PandoraTypes;
    if (pandoraId && (pandoraId.startsWith(ARTIST) || pandoraId.startsWith(COMPOSER))) {
      data.shareArtistId = pandoraId;
    }
  }
  return data;
}

export function getShareSongData(state, childId) {
  const data = {};
  const allAnnotations = getAllAnnotations(state);
  const trackAnnotations = childId && allAnnotations.get(childId);
  if (trackAnnotations) {
    data.childSourceType = Constants.SourceTypes.PandoraTypes.SONG;
    data.childName = trackAnnotations.get('name');
    data.childCreatorName = trackAnnotations.get('artistName');
    data.childImgUrl = getArt(
      trackAnnotations,
      Constants.Grid.GridConstants.GRID_ITEM_IMAGE_SIZE,
      state,
    );
    data.childDuration = formatTime(trackAnnotations.get('duration'));
    data.childSourcePath = trackAnnotations.get('shareableUrlPath');
    data.childsParentPandoraId = trackAnnotations.get('albumId');
    data.childCreatorId = trackAnnotations.get('artistId');
    const trackArtistAnnotations = allAnnotations.get(data.childCreatorId);
    if (trackArtistAnnotations) {
      data.childCreatorTwitterHandle = trackArtistAnnotations.get('twitterHandle');
    } else {
      data.needChildAnnotations = true;
    }
    // If sharing a track, and parent album is a single, hide parent in share modal
    if (data.parentTrackCount === 1) {
      data.hideParent = true;
    }
  }
  return data;
}

export function getShareArtistData(state, sourceId) {
  const data = {};
  const allAnnotations = getAllAnnotations(state);
  const artistAnnotations = allAnnotations.get(sourceId);
  if (artistAnnotations) {
    data.sourcePath = artistAnnotations.get('shareableUrlPath');
    data.sourceName = artistAnnotations.get('name');
    data.imgUrl = getArt(
      artistAnnotations,
      Constants.Grid.GridConstants.GRID_ITEM_IMAGE_SIZE,
      state,
    );
    data.creatorTwitterHandle = artistAnnotations.get('twitterHandle');
  }
  return data;
}

export function needAnnotation(props) {
  const { mainText, imgUrl, sourcePath } = props;

  return isNil(mainText) || isNil(imgUrl) || isNil(sourcePath);
}

export function needTrackProps(props) {
  const {
    childName,
    childImgUrl,
    childCreatorName,
    childDuration,
    childSourcePath,
    parentTrackCount,
  } = props;

  return (
    isNil(childName) ||
    isNil(childImgUrl) ||
    isNil(childCreatorName) ||
    isNil(childDuration) ||
    isNil(childSourcePath) ||
    isNil(parentTrackCount)
  );
}

export function needStationProps(props) {
  const { parentSourceName, parentImgUrl, parentSourcePath } = props;

  return isNil(parentSourceName) || isNil(parentImgUrl) || isNil(parentSourcePath);
}

export function needAlbumProps(props) {
  const { parentSourceName, parentImgUrl, parentCreatorName, parentTrackCount, parentSourcePath } =
    props;

  return (
    isNil(parentSourceName) ||
    isNil(parentImgUrl) ||
    isNil(parentCreatorName) ||
    isNil(parentTrackCount) ||
    isNil(parentSourcePath)
  );
}

export function needPlaylistProps(props) {
  const { parentSourcePath, parentSourceName, parentCreatorName, parentImgUrl, parentTrackCount } =
    props;

  return (
    isNil(parentSourcePath) ||
    isNil(parentSourceName) ||
    isNil(parentCreatorName) ||
    isNil(parentImgUrl) ||
    isNil(parentTrackCount)
  );
}

export function needPodcastProps(props) {
  const {
    parentSourceName,
    parentImgUrl,
    parentCreatorName,
    parentEpisodeCount,
    parentSourcePath,
  } = props;

  return (
    isNil(parentSourceName) ||
    isNil(parentImgUrl) ||
    isNil(parentCreatorName) ||
    isNil(parentEpisodeCount) ||
    isNil(parentSourcePath)
  );
}

export function needEpisodeProps(props) {
  const {
    parentSourceName,
    parentSourcePath,
    parentImgUrl,
    parentCreatorName,
    childReleaseDate,
    childDuration,
  } = props;

  return (
    isNil(parentSourceName) ||
    isNil(parentSourcePath) ||
    isNil(parentImgUrl) ||
    isNil(parentCreatorName) ||
    isNil(childReleaseDate) ||
    isNil(childDuration)
  );
}

export function isSong(sourceId) {
  return sourceId && sourceId.startsWith(Constants.SourceTypes.PandoraTypes.SONG);
}

export function isEpisode(sourceId) {
  return sourceId && sourceId.startsWith(Constants.SourceTypes.PandoraTypes.EPISODE);
}

export function getTwitterBoilerplateHelper(
  props,
  sourceType,
  sourceName,
  creatorName,
  shareURL,
  creatorTwitterHandle,
) {
  const {
    intl: { formatMessage: t },
  } = props;
  const { messages } = Constants.ShareModal;
  let message;
  if (sourceType === Constants.SourceTypes.PandoraTypes.ARTIST) {
    message = messages.twitterBoilerplateArtist;
  } else {
    message =
      creatorTwitterHandle || creatorName
        ? messages.twitterBoilerplate
        : messages.twitterBoilerplateNoArtist;
  }
  return t(message, {
    sourceName,
    sourcePath: shareURL,
    artist: creatorTwitterHandle ? `@${creatorTwitterHandle}` : creatorName,
  });
}

function getShareData(state, sourceId, sourceType) {
  const { messages } = Constants.ShareModal;

  let data;
  let shareData = {};
  switch (sourceType) {
    case Constants.SourceTypes.PandoraTypes.SONG:
      data = getShareSongData(state, sourceId);
      shareData = {
        mainText: data.childName,
        secondText: data.childCreatorName ? (
          <FormattedMessage
            {...messages.byPodcastCreator}
            values={{ publisher: data.childCreatorName }}
          />
        ) : null,
        thirdText: (
          <FormattedMessage {...messages.trackLength} values={{ length: data.childDuration }} />
        ),
        imgUrl: data.childImgUrl,
        sourcePath: data.childSourcePath,
        creatorName: data.childCreatorName,
        creatorTwitterHandle: data.childCreatorTwitterHandle,
        shareArtistId: data.childCreatorId,
      };
      break;
    case Constants.SourceTypes.PandoraTypes.EPISODE:
      data = getShareEpisodeData(state, sourceId);
      shareData = {
        mainText: data.childName,
        secondText: data.childCreatorName ? (
          <FormattedMessage
            {...messages.byPodcastCreator}
            values={{ publisher: data.childCreatorName }}
          />
        ) : null,
        thirdText: (
          <PodcastEpisodeMetadata
            releaseDate={data.childReleaseDate}
            episodeDuration={data.childDuration}
          />
        ),
        imgUrl: data.childImgUrl,
        sourcePath: data.childSourcePath,
        creatorName: null,
        creatorTwitterHandle: null,
        shareArtistId: null,
      };
      break;
    case Constants.SourceTypes.PandoraTypes.STATION:
      data = getShareStationData(state, sourceId);
      shareData = {
        mainText: data.parentSourceName,
        secondText: null,
        thirdText: <FormattedMessage {...messages.station} />,
        imgUrl: data.parentImgUrl,
        sourcePath: data.parentSourcePath,
        creatorName: null,
        creatorTwitterHandle: null,
        shareArtistId: data.shareArtistId,
      };
      break;
    case Constants.SourceTypes.PandoraTypes.ALBUM:
      data = getShareAlbumData(state, sourceId);
      shareData = {
        mainText: data.parentSourceName,
        secondText: (
          <FormattedMessage {...messages.byArtist} values={{ artist: data.parentCreatorName }} />
        ),
        thirdText: data.parentTrackCount ? (
          <FormattedMessage
            {...messages.trackCountAlbum}
            values={{ trackCount: data.parentTrackCount }}
          />
        ) : null,
        imgUrl: data.parentImgUrl,
        sourcePath: data.parentSourcePath,
        creatorName: data.parentCreatorName,
        creatorTwitterHandle: data.albumArtistTwitterHandle,
        shareArtistId: data.albumArtistId,
      };
      break;
    case Constants.SourceTypes.PandoraTypes.PLAYLIST:
      data = getSharePlaylistData(state, sourceId);
      shareData = {
        mainText: data.parentSourceName,
        secondText: (
          <FormattedMessage {...messages.byPlaylist} values={{ artist: data.parentCreatorName }} />
        ),
        thirdText: data.parentTrackCount ? (
          <FormattedMessage
            {...messages.trackCount}
            values={{ trackCount: data.parentTrackCount }}
          />
        ) : null,
        imgUrl: data.parentImgUrl,
        sourcePath: data.parentSourcePath,
        creatorName: data.parentCreatorName,
        creatorTwitterHandle: null,
        shareArtistId: null,
      };
      break;
    case Constants.SourceTypes.PandoraTypes.PODCAST:
      data = getSharePodcastData(state, sourceId);
      shareData = {
        mainText: data.parentSourceName,
        secondText: data.parentCreatorName,
        thirdText: data.parentEpisodeCount ? (
          <FormattedMessage
            {...messages.episodeCount}
            values={{ episodeCount: data.parentEpisodeCount }}
          />
        ) : null,
        imgUrl: data.parentImgUrl,
        sourcePath: data.parentSourcePath,
        creatorName: data.parentCreatorName,
        creatorTwitterHandle: null,
        shareArtistId: null,
      };
      break;
    case Constants.SourceTypes.PandoraTypes.ARTIST:
    case Constants.SourceTypes.PandoraTypes.COMPOSER:
      data = getShareArtistData(state, sourceId);
      shareData = {
        mainText: data.sourceName,
        secondText: Constants.ShareModal.ArtistLabel,
        thirdText: null,
        imgUrl: data.imgUrl,
        sourcePath: data.sourcePath,
        creatorName: data.sourceName,
        creatorTwitterHandle: data.creatorTwitterHandle,
        shareArtistId: null,
      };
      break;
    case Constants.SourceTypes.PandoraTypes.CURATOR:
      data = getShareArtistData(state, sourceId);
      shareData = {
        mainText: data.sourceName,
        secondText: Constants.ShareModal.CuratorLabel,
        thirdText: null,
        imgUrl: data.imgUrl,
        sourcePath: data.sourcePath,
        creatorName: data.sourceName,
        creatorTwitterHandle: data.creatorTwitterHandle,
        shareArtistId: null,
      };
      break;
  }

  return shareData;
}

export function generateShareTabObject(state, sourceId, sourceType) {
  const { shareMessages } = Constants.ShareModal;
  const message = shareMessages.sourceType;
  const tabContent = getShareData(state, sourceId, sourceType);

  return {
    sourceId,
    sourceType,
    tab: {
      label: <FormattedMessage {...message} values={{ sourceType }} />,
      url: sourceId,
      type: 'button',
    },
    tabContent,
  };
}

export function generateShareTabObjects(state, params) {
  const { childId, parentSourceId, parentSourceType } = params;
  const tabObjects = [];
  let shareArtistId = null;

  if (isSong(childId) || isEpisode(childId)) {
    let sourceType;
    if (isSong(childId)) {
      sourceType = Constants.SourceTypes.PandoraTypes.SONG;
    } else if (isEpisode(childId)) {
      sourceType = Constants.SourceTypes.PandoraTypes.EPISODE;
    }

    const tabObject = generateShareTabObject(state, childId, sourceType);
    tabObjects.push(tabObject);
    if (tabObject.tabContent.shareArtistId) {
      shareArtistId = tabObject.tabContent.shareArtistId;
    }
  }

  const parentSourceTypes = [
    Constants.SourceTypes.PandoraTypes.STATION,
    Constants.SourceTypes.PandoraTypes.ALBUM,
    Constants.SourceTypes.PandoraTypes.PLAYLIST,
    Constants.SourceTypes.PandoraTypes.PODCAST,
    Constants.SourceTypes.PandoraTypes.ARTIST,
    Constants.SourceTypes.PandoraTypes.CURATOR,
  ];
  if (parentSourceId && parentSourceTypes.includes(parentSourceType)) {
    const tabObject = generateShareTabObject(state, parentSourceId, parentSourceType);
    tabObjects.push(tabObject);
    if (tabObject.tabContent.shareArtistId) {
      shareArtistId = tabObject.tabContent.shareArtistId;
    }
  }

  // Ideally we should get artistId from the action
  if (shareArtistId) {
    const artistTabObject = generateShareTabObject(
      state,
      shareArtistId,
      Constants.SourceTypes.PandoraTypes.ARTIST,
    );
    tabObjects.push(artistTabObject);
  }

  return tabObjects;
}
